import React, { useState, useEffect, useMemo } from "react";
import { Download, Layers, Search } from "lucide-react";
import { exportDMCommissionReportToExcel } from "../components/DMCommExport.jsx";
import PeriodSelector from "../components/PeriodSelector.jsx";
import { Link } from "react-router-dom";
import { getDistrictManagerCommissions } from "../services/districtManagersApi.js";

const formatNumber = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const formatCurrency = (num) => `$${formatNumber(num)}`;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const periodToMonthNumber = (period) => {
  const match = period?.match(/month-(\d+)/);
  return match ? Number(match[1]) : null;
};

const isQuarterPeriod = (period) => /^q[1-4]$/i.test(period ?? "");
const isYtdPeriod = (period) => period === "ytd";

const parsePeriodType = (period) => {
  const monthNum = periodToMonthNumber(period);
  if (monthNum !== null) return { periodType: "month", month: monthNum };
  if (isQuarterPeriod(period))
    return { periodType: "quarter", quarter: period.toLowerCase() };
  if (isYtdPeriod(period)) return { periodType: "ytd" };
  return { periodType: "month", month: new Date().getMonth() + 1 };
};

const buildPeriodLabel = (year, period) => {
  const monthNum = periodToMonthNumber(period);
  if (monthNum !== null) return `${MONTHS[monthNum - 1]} ${year}`;
  if (isQuarterPeriod(period)) return `${period.toUpperCase()} ${year}`;
  if (isYtdPeriod(period)) return `YTD ${year}`;
  return String(year);
};

const DistrictManagerCommissions = () => {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1);

  const [year, setYear] = useState(prevMonth.getFullYear());
  const [period, setPeriod] = useState(`month-${prevMonth.getMonth() + 1}`);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [activeDM, setActiveDM] = useState(null);

  // Split accounting manual states
  const [adjustments, setAdjustments] = useState({});
  const [bonuses, setBonuses] = useState({});

  const periodLabel = buildPeriodLabel(year, period);

  const fetchData = async () => {
    setLoading(true);
    setActiveDM(null);
    try {
      const {
        periodType,
        month: monthNum,
        quarter: qStr,
      } = parsePeriodType(period);
      const data = await getDistrictManagerCommissions({
        year,
        periodType,
        month: monthNum,
        quarter: qStr,
      });

      setStores(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error("Failed to fetch DM report:", err);
      setStores([]);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  const dms = useMemo(() => {
    const map = new Map();
    stores.forEach((store) => {
      (store.districtManagers || []).forEach((dm) => {
        map.set(dm.districtManagerId, dm.name);
      });
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [stores]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dms.length && !activeDM) setActiveDM(dms[0].id);
  }, [dms, activeDM]);

  const rows = useMemo(() => {
    if (!activeDM) return [];
    return stores.flatMap((store) =>
      (store.districtManagers || [])
        .filter((dm) => dm.districtManagerId === Number(activeDM))
        .map((dm) => ({ ...store, dm })),
    );
  }, [stores, activeDM]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.sales += r.currentSales || 0;
          acc.commission += r.dm.commission || 0;
          acc.quota += r.quota || 0;
          acc.percentChange += r.percentChange || 0;
          acc.count += 1;
          return acc;
        },
        { sales: 0, commission: 0, quota: 0, percentChange: 0, count: 0 },
      ),
    [rows],
  );

  // const exportToExcel = () => {
  //   if (!stores.length) return;
  //   const exportRows = stores.flatMap((store) =>
  //     (store.districtManagers || []).map((dm) => {
  //       const adj = adjustments[dm.districtManagerId] ?? 0;
  //       const bns = bonuses[dm.districtManagerId] ?? 0;
  //       return {
  //         store: store.store,
  //         sales: store.currentSales,
  //         quota: store.quota,
  //         quotaPercent: store.quotaPercent,
  //         percentChange: store.percentChange,
  //         tier: store.tier,
  //         multiplier: dm.multiplier,
  //         dmName: dm.name,
  //         commissionRate: dm.commissionRate,
  //         commission: dm.commission,
  //         bonus: bns,
  //         adjustment: adj,
  //         totalCommission: dm.commission + adj + bns,
  //       };
  //     }),
  //   );
  //   exportDMCommissionReportToExcel(exportRows);
  // };

  const exportToExcel = () => {
    if (!stores.length) return;
    const exportRows = stores.flatMap((store) =>
      (store.districtManagers || []).map((dm) => {
        const adj = adjustments[dm.districtManagerId] ?? 0;
        const bns = bonuses[dm.districtManagerId] ?? 0;
        return {
          store: store.store,
          sales: store.currentSales,
          quota: store.quota,
          quotaPercent: store.quotaPercent,
          percentChange: store.percentChange,
          tier: store.tier,
          multiplier: dm.multiplier,
          dmName: dm.name,
          commissionRate: dm.commissionRate,
          commission: dm.commission,
          bonus: bns,
          adjustment: adj,
          totalCommission: dm.commission + adj + bns,
        };
      }),
    );
    // Added metadata object as 3rd parameter
    exportDMCommissionReportToExcel(exportRows, undefined, { periodLabel });
  };

  const activeDMName = dms.find((dm) => dm.id === activeDM)?.name || "";
  const quotaPercentTotal =
    totals.quota > 0 ? (totals.sales / totals.quota) * 100 : 0;

  // Track values safely as fallback numbers
  const activeAdj = adjustments[activeDM] ?? 0;
  const activeBns = bonuses[activeDM] ?? 0;
  const hasModifiers = activeAdj !== 0 || activeBns !== 0;

  return (
    <div className="min-h-screen bg-slate-50 p-6 text-slate-900 font-sans">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Toolbar */}
        <div className="bg-white rounded border border-slate-200 px-5 py-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="mr-auto">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">
                District Manager Commissions
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {hasFetched
                  ? `${periodLabel} Performance Report`
                  : "Select a period and click View to load data"}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Year
              </label>
              <input
                type="number"
                min="2000"
                max="2100"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="border border-slate-300 rounded px-2 py-1 w-24 text-base font-medium focus:outline-none focus:border-slate-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Period
              </label>
              <PeriodSelector period={period} setPeriod={setPeriod} />
            </div>

            <div className="h-6 w-px bg-slate-200 hidden lg:block" />

            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-sm font-semibold uppercase tracking-wider text-white rounded transition"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              View
            </button>

            <Link
              to="/dm-comm-tiers"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition"
            >
              <Layers className="w-4 h-4" />
              Tiers
            </Link>

            <button
              onClick={exportToExcel}
              disabled={!stores.length}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white rounded text-sm font-semibold uppercase tracking-wider transition"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Empty Placeholder */}
        {!hasFetched && !loading && (
          <div className="bg-white rounded border border-slate-200 py-20 text-center text-slate-400 shadow-sm">
            <Search className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-base font-semibold text-slate-700">
              No Report Data Loaded
            </p>
            <p className="text-sm mt-0.5">
              Select performance parameters and execute search view.
            </p>
          </div>
        )}

        {hasFetched && (
          <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
            {/* Tabs */}
            {dms.length > 0 && (
              <div className="flex overflow-x-auto border-b border-slate-200 bg-slate-50">
                {dms.map((dm) => (
                  <button
                    key={dm.id}
                    onClick={() => setActiveDM(dm.id)}
                    className={`px-6 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
                      activeDM === dm.id
                        ? "border-slate-800 text-slate-900 bg-white"
                        : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                    }`}
                  >
                    {dm.name}
                  </button>
                ))}
              </div>
            )}

            {/* Table Header Wrapper */}
            <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between">
              <h2 className="text-base font-bold uppercase tracking-wider text-slate-700">
                Store Performance Detail
                {activeDMName && (
                  <span className="text-slate-400 font-normal normal-case">
                    {" "}
                    — Sheet: {activeDMName}
                  </span>
                )}
              </h2>
              {rows.length > 0 && (
                <span className="text-sm font-mono text-slate-500">
                  Count: {rows.length} Record{rows.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {rows.length === 0 ? (
              <div className="px-6 py-14 text-center text-slate-400">
                <p className="text-base">
                  No ledger item data for selected entity.
                </p>
              </div>
            ) : (
              <div className="overflow-auto max-h-[75vh]">
                <table className="w-full border-collapse font-mono text-sm">
                  <thead className="bg-slate-50 border-b border-slate-300 sticky top-0 z-20">
                    <tr>
                      {[
                        ["Store", "text-left"],
                        ["Sales", "text-right"],
                        ["Quota", "text-right"],
                        ["Quota %", "text-right"],
                        ["Var %", "text-right"],
                        ["Tier", "text-center"],
                        ["Rate", "text-right"],
                        ["Multiplier", "text-right"],
                        ["Commission", "text-right"],
                      ].map(([label, alignClass]) => (
                        <th
                          key={label}
                          className={`px-4 py-3 ${alignClass} text-xs font-bold uppercase tracking-wider text-slate-600 bg-slate-50 border-b border-slate-200`}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {rows.map((r) => {
                      const isHit = r.quotaPercent >= 99;
                      const pctChange =
                        r.percentChange < 0
                          ? `(${Math.abs(r.percentChange).toFixed(2)}%)`
                          : `${r.percentChange.toFixed(2)}%`;

                      return (
                        <tr
                          key={r.store}
                          className={`transition-colors ${
                            isHit
                              ? "bg-emerald-50/60 hover:bg-emerald-50"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <td className="px-4 py-3 font-sans font-medium text-slate-900">
                            {r.store}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {formatCurrency(r.currentSales)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {formatCurrency(r.quota)}
                          </td>
                          <td
                            className={`px-4 py-3 text-right tabular-nums ${isHit ? "text-emerald-800 font-bold" : ""}`}
                          >
                            {r.quotaPercent.toFixed(1)}%
                          </td>
                          <td
                            className={`px-4 py-3 text-right tabular-nums ${r.percentChange < 0 ? "text-red-600" : ""}`}
                          >
                            {pctChange}
                          </td>
                          <td className="px-4 py-3 text-center tabular-nums">
                            {r.tier}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {r.dm.commissionRate.toFixed(2)}%
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            x{Number(r.dm.multiplier || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                            {formatCurrency(r.dm.commission)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Accounting Footers */}
                  <tfoot>
                    {/* Totals Row with Single Accounting Top Border */}
                    <tr className="bg-slate-50/50 border-t-2 border-slate-400">
                      <td className="px-4 py-3 font-sans font-bold text-slate-700">
                        Total Subtotal
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-900">
                        {formatCurrency(totals.sales)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-600">
                        {formatCurrency(totals.quota)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-900">
                        {quotaPercentTotal.toFixed(2)}%
                      </td>
                      <td colSpan={4} />
                      <td
                        className={`px-4 py-3 text-right tabular-nums font-bold text-slate-900 ${!hasModifiers ? "border-b-4 border-double border-slate-900" : ""}`}
                      >
                        {formatCurrency(totals.commission)}
                      </td>
                    </tr>

                    {/* FIELD 1: Manual Adjustment Row */}
                    <tr className="bg-white border-t border-slate-100">
                      <td
                        colSpan={8}
                        className="px-4 py-3 font-sans text-sm font-semibold text-slate-600 text-right"
                      >
                        Adjustment
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end border border-slate-300 rounded px-2 py-1 w-fit ml-auto bg-white">
                          <span className="text-slate-400 mr-0.5">$</span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={activeAdj || ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? 0
                                  : parseFloat(e.target.value);
                              setAdjustments((prev) => ({
                                ...prev,
                                [activeDM]: isNaN(value) ? 0 : value,
                              }));
                            }}
                            className="w-28 text-right bg-transparent font-mono text-sm font-semibold text-slate-900 focus:outline-none"
                          />
                        </div>
                      </td>
                    </tr>

                    {/* FIELD 2: Manual Bonus Row */}
                    <tr className="bg-white border-t border-slate-200">
                      <td
                        colSpan={8}
                        className="px-4 py-3 font-sans text-sm font-semibold text-slate-600 text-right"
                      >
                        Bonus
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end border border-slate-300 rounded px-2 py-1 w-fit ml-auto bg-white">
                          <span className="text-slate-400 mr-0.5">$</span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={activeBns || ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? 0
                                  : parseFloat(e.target.value);
                              setBonuses((prev) => ({
                                ...prev,
                                [activeDM]: isNaN(value) ? 0 : value,
                              }));
                            }}
                            className="w-28 text-right bg-transparent font-mono text-sm font-semibold text-slate-900 focus:outline-none"
                          />
                        </div>
                      </td>
                    </tr>

                    {/* Grand Total Row with Accounting Double Line Underline */}
                    {hasModifiers && (
                      <tr className="bg-slate-100/50 border-t border-slate-300">
                        <td
                          colSpan={8}
                          className="px-4 py-3 font-sans text-sm font-bold text-slate-900 text-right uppercase tracking-wider"
                        >
                          Net Commission
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-extrabold text-slate-950 border-b-4 border-double border-slate-900 bg-slate-50">
                          {formatCurrency(
                            totals.commission + activeAdj + activeBns,
                          )}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DistrictManagerCommissions;

// import React, { useState, useEffect, useMemo } from "react";
// import { Download, Layers, Search } from "lucide-react";
// import { exportDMCommissionReportToExcel } from "../components/DMCommExport.jsx";
// import PeriodSelector from "../components/PeriodSelector.jsx";
// import { Link } from "react-router-dom";
// import { getDistrictManagerCommissions } from "../services/districtManagersApi.js";

// const formatNumber = (num) =>
//   Number(num || 0).toLocaleString(undefined, {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });
// const formatCurrency = (num) => `$${formatNumber(num)}`;

// const MONTHS = [
//   "January",
//   "February",
//   "March",
//   "April",
//   "May",
//   "June",
//   "July",
//   "August",
//   "September",
//   "October",
//   "November",
//   "December",
// ];

// const periodToMonthNumber = (period) => {
//   const match = period?.match(/month-(\d+)/);
//   return match ? Number(match[1]) : null;
// };

// const isQuarterPeriod = (period) => /^q[1-4]$/i.test(period ?? "");
// const isYtdPeriod = (period) => period === "ytd";

// const parsePeriodType = (period) => {
//   const monthNum = periodToMonthNumber(period);
//   if (monthNum !== null) return { periodType: "month", month: monthNum };
//   if (isQuarterPeriod(period))
//     return { periodType: "quarter", quarter: period.toLowerCase() };
//   if (isYtdPeriod(period)) return { periodType: "ytd" };
//   return { periodType: "month", month: new Date().getMonth() + 1 };
// };

// const buildPeriodLabel = (year, period) => {
//   const monthNum = periodToMonthNumber(period);
//   if (monthNum !== null) return `${MONTHS[monthNum - 1]} ${year}`;
//   if (isQuarterPeriod(period)) return `${period.toUpperCase()} ${year}`;
//   if (isYtdPeriod(period)) return `YTD ${year}`;
//   return String(year);
// };

// const DistrictManagerCommissions = () => {
//   const now = new Date();
//   const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1);

//   const [year, setYear] = useState(prevMonth.getFullYear());
//   const [period, setPeriod] = useState(`month-${prevMonth.getMonth() + 1}`);
//   const [stores, setStores] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [hasFetched, setHasFetched] = useState(false);
//   const [activeDM, setActiveDM] = useState(null);

//   // Split accounting manual states
//   const [adjustments, setAdjustments] = useState({});
//   const [bonuses, setBonuses] = useState({});

//   const periodLabel = buildPeriodLabel(year, period);

//   const fetchData = async () => {
//     setLoading(true);
//     setActiveDM(null);
//     try {
//       const {
//         periodType,
//         month: monthNum,
//         quarter: qStr,
//       } = parsePeriodType(period);
//       const data = await getDistrictManagerCommissions({
//         year,
//         periodType,
//         month: monthNum,
//         quarter: qStr,
//       });

//       setStores(Array.isArray(data) ? data : data?.data || []);
//     } catch (err) {
//       console.error("Failed to fetch DM report:", err);
//       setStores([]);
//     } finally {
//       setLoading(false);
//       setHasFetched(true);
//     }
//   };

//   const dms = useMemo(() => {
//     const map = new Map();
//     stores.forEach((store) => {
//       (store.districtManagers || []).forEach((dm) => {
//         map.set(dm.districtManagerId, dm.name);
//       });
//     });
//     return [...map.entries()].map(([id, name]) => ({ id, name }));
//   }, [stores]);

//   useEffect(() => {
//     fetchData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   useEffect(() => {
//     if (dms.length && !activeDM) setActiveDM(dms[0].id);
//   }, [dms, activeDM]);

//   const rows = useMemo(() => {
//     if (!activeDM) return [];
//     return stores.flatMap((store) =>
//       (store.districtManagers || [])
//         .filter((dm) => dm.districtManagerId === Number(activeDM))
//         .map((dm) => ({ ...store, dm })),
//     );
//   }, [stores, activeDM]);

//   const totals = useMemo(
//     () =>
//       rows.reduce(
//         (acc, r) => {
//           acc.sales += r.currentSales || 0;
//           acc.commission += r.dm.commission || 0;
//           acc.quota += r.quota || 0;
//           acc.percentChange += r.percentChange || 0;
//           acc.count += 1;
//           return acc;
//         },
//         { sales: 0, commission: 0, quota: 0, percentChange: 0, count: 0 },
//       ),
//     [rows],
//   );

//   const exportToExcel = () => {
//     if (!stores.length) return;
//     const exportRows = stores.flatMap((store) =>
//       (store.districtManagers || []).map((dm) => {
//         const adj = adjustments[dm.districtManagerId] ?? 0;
//         const bns = bonuses[dm.districtManagerId] ?? 0;
//         return {
//           store: store.store,
//           sales: store.currentSales,
//           quota: store.quota,
//           quotaPercent: store.quotaPercent,
//           percentChange: store.percentChange,
//           tier: store.tier,
//           multiplier: dm.multiplier,
//           dmName: dm.name,
//           commissionRate: dm.commissionRate,
//           commission: dm.commission,
//           bonus: bns,
//           adjustment: adj,
//           totalCommission: dm.commission + adj + bns,
//         };
//       }),
//     );
//     exportDMCommissionReportToExcel(exportRows);
//   };

//   const activeDMName = dms.find((dm) => dm.id === activeDM)?.name || "";
//   const quotaPercentTotal =
//     totals.quota > 0 ? (totals.sales / totals.quota) * 100 : 0;

//   // Track values safely as fallback numbers
//   const activeAdj = adjustments[activeDM] ?? 0;
//   const activeBns = bonuses[activeDM] ?? 0;
//   const hasModifiers = activeAdj !== 0 || activeBns !== 0;

//   return (
//     <div className="min-h-screen bg-slate-50 p-6 text-slate-900 font-sans">
//       <div className="max-w-7xl mx-auto space-y-4">
//         {/* Toolbar */}
//         <div className="bg-white rounded border border-slate-200 px-5 py-4 shadow-sm">
//           <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
//             <div className="mr-auto">
//               <h1 className="text-xl font-bold tracking-tight text-slate-900">
//                 District Manager Commissions
//               </h1>
//               <p className="text-slate-500 text-sm mt-0.5">
//                 {hasFetched
//                   ? `${periodLabel} Performance Report`
//                   : "Select a period and click View to load data"}
//               </p>
//             </div>

//             <div className="flex items-center gap-2">
//               <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
//                 Year
//               </label>
//               <input
//                 type="number"
//                 min="2000"
//                 max="2100"
//                 value={year}
//                 onChange={(e) => setYear(Number(e.target.value))}
//                 className="border border-slate-300 rounded px-2 py-1 w-24 text-base font-medium focus:outline-none focus:border-slate-500"
//               />
//             </div>

//             <div className="flex items-center gap-2">
//               <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
//                 Period
//               </label>
//               <PeriodSelector period={period} setPeriod={setPeriod} />
//             </div>

//             <div className="h-6 w-px bg-slate-200 hidden lg:block" />

//             <button
//               onClick={fetchData}
//               disabled={loading}
//               className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-sm font-semibold uppercase tracking-wider text-white rounded transition"
//             >
//               {loading ? (
//                 <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
//               ) : (
//                 <Search className="w-4 h-4" />
//               )}
//               View
//             </button>

//             <Link
//               to="/dm-comm-tiers"
//               className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition"
//             >
//               <Layers className="w-4 h-4" />
//               Tiers
//             </Link>

//             <button
//               onClick={exportToExcel}
//               disabled={!stores.length}
//               className="flex items-center gap-1.5 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white rounded text-sm font-semibold uppercase tracking-wider transition"
//             >
//               <Download className="w-4 h-4" />
//               Export
//             </button>
//           </div>
//         </div>

//         {/* Empty Placeholder */}
//         {!hasFetched && !loading && (
//           <div className="bg-white rounded border border-slate-200 py-20 text-center text-slate-400 shadow-sm">
//             <Search className="w-10 h-10 mx-auto mb-2 opacity-40" />
//             <p className="text-base font-semibold text-slate-700">
//               No Report Data Loaded
//             </p>
//             <p className="text-sm mt-0.5">
//               Select performance parameters and execute search view.
//             </p>
//           </div>
//         )}

//         {hasFetched && (
//           <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
//             {/* Tabs */}
//             {dms.length > 0 && (
//               <div className="flex overflow-x-auto border-b border-slate-200 bg-slate-50">
//                 {dms.map((dm) => (
//                   <button
//                     key={dm.id}
//                     onClick={() => setActiveDM(dm.id)}
//                     className={`px-6 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
//                       activeDM === dm.id
//                         ? "border-slate-800 text-slate-900 bg-white"
//                         : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
//                     }`}
//                   >
//                     {dm.name}
//                   </button>
//                 ))}
//               </div>
//             )}

//             {/* Table Header Wrapper */}
//             <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between">
//               <h2 className="text-base font-bold uppercase tracking-wider text-slate-700">
//                 Store Performance Detail
//                 {activeDMName && (
//                   <span className="text-slate-400 font-normal normal-case">
//                     {" "}
//                     — Sheet: {activeDMName}
//                   </span>
//                 )}
//               </h2>
//               {rows.length > 0 && (
//                 <span className="text-sm font-mono text-slate-500">
//                   Count: {rows.length} Record{rows.length !== 1 ? "s" : ""}
//                 </span>
//               )}
//             </div>

//             {rows.length === 0 ? (
//               <div className="px-6 py-14 text-center text-slate-400">
//                 <p className="text-base">
//                   No ledger item data for selected entity.
//                 </p>
//               </div>
//             ) : (
//               <div className="overflow-auto max-h-[75vh]">
//                 <table className="w-full border-collapse font-mono text-sm">
//                   <thead className="bg-slate-50 border-b border-slate-300 sticky top-0 z-20">
//                     <tr>
//                       {[
//                         ["Store", "text-left"],
//                         ["Sales", "text-right"],
//                         ["Quota", "text-right"],
//                         ["Quota %", "text-right"],
//                         ["Var %", "text-right"],
//                         ["Tier", "text-center"],
//                         ["Rate", "text-right"],
//                         ["Multiplier", "text-right"],
//                         ["Commission", "text-right"],
//                       ].map(([label, alignClass]) => (
//                         <th
//                           key={label}
//                           className={`px-4 py-3 ${alignClass} text-xs font-bold uppercase tracking-wider text-slate-600 bg-slate-50 border-b border-slate-200`}
//                         >
//                           {label}
//                         </th>
//                       ))}
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y divide-slate-100 text-slate-800">
//                     {rows.map((r) => {
//                       const isHit = r.quotaPercent >= 99;
//                       const pctChange =
//                         r.percentChange < 0
//                           ? `(${Math.abs(r.percentChange).toFixed(2)}%)`
//                           : `${r.percentChange.toFixed(2)}%`;

//                       return (
//                         <tr
//                           key={r.store}
//                           className={`transition-colors ${
//                             isHit
//                               ? "bg-emerald-50/60 hover:bg-emerald-50"
//                               : "hover:bg-slate-50"
//                           }`}
//                         >
//                           <td className="px-4 py-3 font-sans font-medium text-slate-900">
//                             {r.store}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums">
//                             {formatCurrency(r.currentSales)}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {formatCurrency(r.quota)}
//                           </td>
//                           <td
//                             className={`px-4 py-3 text-right tabular-nums ${isHit ? "text-emerald-800 font-bold" : ""}`}
//                           >
//                             {r.quotaPercent.toFixed(1)}%
//                           </td>
//                           <td
//                             className={`px-4 py-3 text-right tabular-nums ${r.percentChange < 0 ? "text-red-600" : ""}`}
//                           >
//                             {pctChange}
//                           </td>
//                           <td className="px-4 py-3 text-center tabular-nums">
//                             {r.tier}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {r.dm.commissionRate.toFixed(2)}%
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             x{Number(r.dm.multiplier || 0).toFixed(2)}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
//                             {formatCurrency(r.dm.commission)}
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>

//                   {/* Accounting Footers */}
//                   <tfoot>
//                     {/* Totals Row with Single Accounting Top Border */}
//                     <tr className="bg-slate-50/50 border-t-2 border-slate-400">
//                       <td className="px-4 py-3 font-sans font-bold text-slate-700">
//                         Total Subtotal
//                       </td>
//                       <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-900">
//                         {formatCurrency(totals.sales)}
//                       </td>
//                       <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-600">
//                         {formatCurrency(totals.quota)}
//                       </td>
//                       <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-900">
//                         {quotaPercentTotal.toFixed(2)}%
//                       </td>
//                       <td colSpan={4} />
//                       <td
//                         className={`px-4 py-3 text-right tabular-nums font-bold text-slate-900 ${!hasModifiers ? "border-b-4 border-double border-slate-900" : ""}`}
//                       >
//                         {formatCurrency(totals.commission)}
//                       </td>
//                     </tr>

//                     {/* FIELD 1: Manual Adjustment Row */}
//                     <tr className="bg-white border-t border-slate-100">
//                       <td
//                         colSpan={8}
//                         className="px-4 py-3 font-sans text-sm font-semibold text-slate-600 text-right"
//                       >
//                         Adjustment
//                       </td>
//                       <td className="px-4 py-2 text-right">
//                         <div className="flex items-center justify-end border border-slate-300 rounded px-2 py-1 w-fit ml-auto bg-white">
//                           <span className="text-slate-400 mr-0.5">$</span>
//                           <input
//                             type="number"
//                             step="0.01"
//                             placeholder="0.00"
//                             value={activeAdj || ""}
//                             onChange={(e) => {
//                               const value =
//                                 e.target.value === ""
//                                   ? 0
//                                   : parseFloat(e.target.value);
//                               setAdjustments((prev) => ({
//                                 ...prev,
//                                 [activeDM]: isNaN(value) ? 0 : value,
//                               }));
//                             }}
//                             className="w-28 text-right bg-transparent font-mono text-sm font-semibold text-slate-900 focus:outline-none"
//                           />
//                         </div>
//                       </td>
//                     </tr>

//                     {/* FIELD 2: Manual Bonus Row */}
//                     <tr className="bg-white border-t border-slate-200">
//                       <td
//                         colSpan={8}
//                         className="px-4 py-3 font-sans text-sm font-semibold text-slate-600 text-right"
//                       >
//                         Bonus
//                       </td>
//                       <td className="px-4 py-2 text-right">
//                         <div className="flex items-center justify-end border border-slate-300 rounded px-2 py-1 w-fit ml-auto bg-white">
//                           <span className="text-slate-400 mr-0.5">$</span>
//                           <input
//                             type="number"
//                             step="0.01"
//                             placeholder="0.00"
//                             value={activeBns || ""}
//                             onChange={(e) => {
//                               const value =
//                                 e.target.value === ""
//                                   ? 0
//                                   : parseFloat(e.target.value);
//                               setBonuses((prev) => ({
//                                 ...prev,
//                                 [activeDM]: isNaN(value) ? 0 : value,
//                               }));
//                             }}
//                             className="w-28 text-right bg-transparent font-mono text-sm font-semibold text-slate-900 focus:outline-none"
//                           />
//                         </div>
//                       </td>
//                     </tr>

//                     {/* Grand Total Row with Accounting Double Line Underline */}
//                     {hasModifiers && (
//                       <tr className="bg-slate-100/50 border-t border-slate-300">
//                         <td
//                           colSpan={8}
//                           className="px-4 py-3 font-sans text-sm font-bold text-slate-900 text-right uppercase tracking-wider"
//                         >
//                           Net Commission
//                         </td>
//                         <td className="px-4 py-3 text-right tabular-nums font-extrabold text-slate-950 border-b-4 border-double border-slate-900 bg-slate-50">
//                           {formatCurrency(
//                             totals.commission + activeAdj + activeBns,
//                           )}
//                         </td>
//                       </tr>
//                     )}
//                   </tfoot>
//                 </table>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DistrictManagerCommissions;
